import { ReactNode, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import { ModalContext } from './ModalContext';
import { ModalOptions, ModalRenderProps } from './types';
import { useCloseOnNavigation } from '@/common/hooks/useCloseOnNavigation';

export interface ModalProviderProps {
  children: ReactNode;
}

interface ModalState<T = unknown> {
  options: ModalOptions<T>;
  renderProps: ModalRenderProps<T>;
}

export const ModalProvider = ({ children }: ModalProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalState, setModalState] = useState<ModalState | null>(null);

  const resolverRef = useRef<((value: unknown) => void) | null>(null);

  const showModal = <T,>(options: ModalOptions<T>): Promise<T | undefined> => {
    return new Promise<T | undefined>((resolve) => {
      resolverRef.current = resolve as (value: unknown) => void;

      const handleResolve = (value?: T) => {
        setIsOpen(false);
        resolverRef.current?.(value);
        resolverRef.current = null;
      };

      setModalState({
        options: options as ModalOptions<unknown>,
        renderProps: { resolve: handleResolve } as ModalRenderProps<unknown>,
      });
      setIsOpen(true);
    });
  };

  const handleNavigationClose = () => {
    // On navigation, close modal without resolving (promise remains pending)
    setIsOpen(false);
    resolverRef.current = null;
  };

  useCloseOnNavigation(handleNavigationClose);

  return (
    <ModalContext value={{ showModal }}>
      {children}
      <Dialog
        open={isOpen}
        maxWidth={modalState?.options.maxWidth ?? 'sm'}
        fullWidth={modalState?.options.fullWidth ?? true}
        onClose={() => modalState?.renderProps.resolve()}
        slotProps={{
          paper: {
            sx: modalState?.options.sx,
          },
        }}
      >
        {modalState && modalState.options.content(modalState.renderProps)}
      </Dialog>
    </ModalContext>
  );
};
